import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactComponent as DeleteIcon } from '../../assets/svg/DeleteIcon.svg';
import { ReactComponent as GoBackButton } from '../../assets/svg/GoBackButton.svg';
import Loading from '../Loading';
import { notify } from '../../utils/util';
import { getAllStruggles, getStruggleContent, disconnectStruggleFromContent } from '../../server/server';
import ConfirmationModal from '../modals/ConfirmationModal';
import CreateRecommendationModal from './CreateRecommendationModal';
import EditRecommendationModal from './EditRecommendationModal';
import AddContentModal from './AddContentModal';

function SingleStruggleRecommendationScreen() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [struggle, setStruggle] = useState<any>(null);
	const [recommendations, setRecommendations] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingSolutions, setLoadingSolutions] = useState(false);
	const [loadingCourses, setLoadingCourses] = useState(false);
	const [loadingQuickTips, setLoadingQuickTips] = useState(false);
	const [loadingJournallingPrompts, setLoadingJournallingPrompts] = useState(false);
	const [addContentModal, setAddContentModal] = useState<{
		open: boolean;
		type: 'solutions' | 'courses' | 'quickTips' | 'journallingPrompts';
		recommendationId: string;
	}>({ open: false, type: 'solutions', recommendationId: '' });
	const [deleteModal, setDeleteModal] = useState<{
		open: boolean;
		itemId: string;
		itemType: 'solution' | 'course' | 'quickTip' | 'journallingPrompt';
		itemName: string;
		recommendationId: string;
	}>({ open: false, itemId: '', itemType: 'solution', itemName: '', recommendationId: '' });

	useEffect(() => {
		if (id) {
			loadData();
		}
	}, [id]);

	const loadData = async () => {
		setLoading(true);

		// Load struggle details
		const strugglesResp = await getAllStruggles();
		if (strugglesResp.status === 200) {
			const foundStruggle = strugglesResp.data?.find((s: any) => s.id === id);
			if (foundStruggle) {
				setStruggle(foundStruggle);
			}
		}

		// Load content for this struggle using new API
		await loadContent();
		setLoading(false);
	};

	const loadContent = async () => {
		if (!id) return;

		const resp = await getStruggleContent(id);
		if (resp.status === 200) {
			const data = resp.data || {};
			// Store the content directly instead of recommendations
			const mockRecommendation = {
				id: id,
				courses: data.courses || [],
				solutions: data.solutions || [],
				quickTips: data.quickTips || [],
				journallingPrompts: data.journallingPrompts || [],
			};
			setRecommendations([mockRecommendation]);
		} else {
			notify('Failed to load content');
		}
	};

	// Keep old function name for backward compatibility but use new loadContent
	const loadRecommendations = loadContent;

	const handleDeleteContent = async () => {
		if (!deleteModal.itemId || !id) return;

		let data: any = {};
		if (deleteModal.itemType === 'solution') {
			data.solutionIds = [deleteModal.itemId];
		} else if (deleteModal.itemType === 'course') {
			data.courseIds = [deleteModal.itemId];
		} else if (deleteModal.itemType === 'quickTip') {
			data.quickTipIds = [deleteModal.itemId];
		} else if (deleteModal.itemType === 'journallingPrompt') {
			data.journallingPromptIds = [deleteModal.itemId];
		}

		// Use new disconnect API
		const resp = await disconnectStruggleFromContent(id, data);
		if (resp.status === 200 || resp.status === 201) {
			notify('Content removed successfully');
			loadContent();
		} else {
			notify('Failed to remove content');
		}
		setDeleteModal({ open: false, itemId: '', itemType: 'solution', itemName: '', recommendationId: '' });
	};

	const openAddModal = (type: 'solutions' | 'courses' | 'quickTips' | 'journallingPrompts', recommendationId: string) => {
		setAddContentModal({ open: true, type, recommendationId });
	};

	const openDeleteModal = (itemId: string, itemType: 'solution' | 'course' | 'quickTip' | 'journallingPrompt', itemName: string, recommendationId: string) => {
		setDeleteModal({ open: true, itemId, itemType, itemName, recommendationId });
	};

	// Get existing IDs for each content type
	const getExistingIds = (type: 'solutions' | 'courses' | 'quickTips' | 'journallingPrompts') => {
		const allIds: string[] = [];
		recommendations.forEach((rec) => {
			if (type === 'solutions' && rec.solutions) {
				rec.solutions.forEach((s: any) => allIds.push(s.id));
			} else if (type === 'courses' && rec.courses) {
				rec.courses.forEach((c: any) => allIds.push(c.id));
			} else if (type === 'quickTips' && rec.quickTips) {
				rec.quickTips.forEach((q: any) => allIds.push(q.id));
			} else if (type === 'journallingPrompts' && rec.journallingPrompts) {
				rec.journallingPrompts.forEach((j: any) => allIds.push(j.id));
			}
		});
		return allIds;
	};

	// Aggregate all content from all recommendations
	const aggregateContent = () => {
		const solutions: any[] = [];
		const courses: any[] = [];
		const quickTips: any[] = [];
		const journallingPrompts: any[] = [];

		recommendations.forEach((rec) => {
			if (rec.solutions) {
				rec.solutions.forEach((s: any) => {
					if (!solutions.find((item) => item.id === s.id)) {
						solutions.push({ ...s, recommendationId: rec.id });
					}
				});
			}
			if (rec.courses) {
				rec.courses.forEach((c: any) => {
					if (!courses.find((item) => item.id === c.id)) {
						courses.push({ ...c, recommendationId: rec.id });
					}
				});
			}
			if (rec.quickTips) {
				rec.quickTips.forEach((q: any) => {
					if (!quickTips.find((item) => item.id === q.id)) {
						quickTips.push({ ...q, recommendationId: rec.id });
					}
				});
			}

			if (rec.journallingPrompts) {
				rec.journallingPrompts.forEach((j: any) => {
					if (!journallingPrompts.find((item) => item.id === j.id)) {
						journallingPrompts.push({ ...j, recommendationId: rec.id });
					}
				});
			}
		});

		return { solutions, courses, quickTips, journallingPrompts };
	};

	const { solutions, courses, quickTips, journallingPrompts } = aggregateContent();

	if (loading) {
		return (
			<div className='flex h-screen flex-col items-center justify-center overflow-y-auto bg-[#ECEBF8]'>
				<Loading size={70} primaryColor='#5E639B' stroke={3} />
			</div>
		);
	}

	return (
		<div className='flex h-screen flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className='w-full max-w-[1200px] px-[10px]'>
				<div className='w-full min-h-[calc(100vh-80px)] bg-white flex flex-col px-[40px] py-[30px] rounded-[10px]'>
					<div className='mb-[30px] flex flex-col gap-4'>
						<div className='flex flex-row items-center gap-2 cursor-pointer w-fit hover:opacity-70' onClick={() => navigate('/struggle-recommendations')}>
							<GoBackButton />
							<span className='text-[16px] font-semibold text-primaryViolet'>Back to Struggles</span>
						</div>

						<div className='flex flex-row items-end gap-4'>
							<div className='flex-1'>
								{struggle && (
									<>
										<p className='text-[40px] leading-[50px] font-bold text-darkViolet'>{struggle.name}</p>
										{struggle.description && <p className='text-[16px] font-medium leading-6 text-mudPurple mt-2'>{struggle.description}</p>}
									</>
								)}
							</div>
						</div>
					</div>

					<div className='grid grid-cols-4 gap-4 mb-4 flex-1'>
						{/* Solutions Column */}
						<div className='flex-1 bg-greyBackground min-h-[100%] p-4 rounded-xl flex flex-col'>
							<div className='relative mb-4'>
								<h3 className='text-center text-[20px] font-semibold text-darkenPurple truncate px-[30px]'>Solutions</h3>
								<button onClick={() => id && openAddModal('solutions', id)} className='absolute right-0 top-0 rounded-full bg-white w-[30px] h-[30px] font-bold text-darkenPurple hover:bg-primaryViolet hover:text-white transition'>
									+
								</button>
							</div>
							<div className='flex-1 overflow-y-auto flex flex-col gap-2'>
								{loadingSolutions ? (
									<div className='flex items-center justify-center py-8'>
										<Loading size={40} primaryColor='#5E639B' stroke={2} />
									</div>
								) : solutions.length === 0 ? (
									<p className='text-center text-mudPurple text-[14px] py-4'>No solutions added yet</p>
								) : (
									solutions.map((solution: any) => (
										<div key={solution.id} className='bg-white rounded-lg p-3 flex flex-col gap-1 group hover:shadow-md transition'>
											<div className='flex justify-between items-start'>
												<div className='flex-1'>
													<p className='text-[14px] font-semibold text-primaryViolet'>{solution.name || solution.title}</p>
													{solution.category?.name && <p className='text-[12px] text-greyMedium mt-1'>{solution.category.name}</p>}
												</div>
												<button onClick={() => openDeleteModal(solution.id, 'solution', solution.name || solution.title, solution.recommendationId)} className='opacity-0 group-hover:opacity-100 transition ml-2'>
													<DeleteIcon className='w-4 h-4 text-red-500 hover:text-red-700' />
												</button>
											</div>
										</div>
									))
								)}
							</div>
						</div>

						{/* Courses Column */}
						<div className='flex-1 bg-greyBackground min-h-[100%] p-4 rounded-xl flex flex-col'>
							<div className='relative mb-4'>
								<h3 className='text-center text-[20px] font-semibold text-darkenPurple truncate px-[30px]'>Courses</h3>
								<button onClick={() => id && openAddModal('courses', id)} className='absolute right-0 top-0 rounded-full bg-white w-[30px] h-[30px] font-bold text-darkenPurple hover:bg-primaryViolet hover:text-white transition'>
									+
								</button>
							</div>
							<div className='flex-1 overflow-y-auto flex flex-col gap-2'>
								{loadingCourses ? (
									<div className='flex items-center justify-center py-8'>
										<Loading size={40} primaryColor='#5E639B' stroke={2} />
									</div>
								) : courses.length === 0 ? (
									<p className='text-center text-mudPurple text-[14px] py-4'>No courses added yet</p>
								) : (
									courses.map((course: any) => (
										<div key={course.id} className='bg-white rounded-lg p-3 flex flex-col gap-1 group hover:shadow-md transition'>
											<div className='flex justify-between items-start'>
												<div className='flex-1'>
													<p className='text-[14px] font-semibold text-primaryViolet'>{course.title}</p>
													{course.category?.name && <p className='text-[12px] text-greyMedium mt-1'>{course.category.name}</p>}
												</div>
												<button onClick={() => openDeleteModal(course.id, 'course', course.title, course.recommendationId)} className='opacity-0 group-hover:opacity-100 transition ml-2'>
													<DeleteIcon className='w-4 h-4 text-red-500 hover:text-red-700' />
												</button>
											</div>
										</div>
									))
								)}
							</div>
						</div>

						{/* Quick Tips Column */}
						<div className='flex-1 bg-greyBackground min-h-[100%] p-4 rounded-xl flex flex-col'>
							<div className='relative mb-4'>
								<h3 className='text-center text-[20px] font-semibold text-darkenPurple truncate px-[30px]'>Quick Tips</h3>
								<button onClick={() => id && openAddModal('quickTips', id)} className='absolute right-0 top-0 rounded-full bg-white w-[30px] h-[30px] font-bold text-darkenPurple hover:bg-primaryViolet hover:text-white transition'>
									+
								</button>
							</div>
							<div className='flex-1 overflow-y-auto flex flex-col gap-2'>
								{loadingQuickTips ? (
									<div className='flex items-center justify-center py-8'>
										<Loading size={40} primaryColor='#5E639B' stroke={2} />
									</div>
								) : quickTips.length === 0 ? (
									<p className='text-center text-mudPurple text-[14px] py-4'>No quick tips added yet</p>
								) : (
									quickTips.map((tip: any) => (
										<div key={tip.id} className='bg-white rounded-lg p-3 flex flex-col gap-1 group hover:shadow-md transition'>
											<div className='flex justify-between items-start'>
												<div className='flex-1'>
													<p className='text-[14px] font-semibold text-primaryViolet'>{tip.title}</p>
												</div>
												<button onClick={() => openDeleteModal(tip.id, 'quickTip', tip.title, tip.recommendationId)} className='opacity-0 group-hover:opacity-100 transition ml-2'>
													<DeleteIcon className='w-4 h-4 text-red-500 hover:text-red-700' />
												</button>
											</div>
										</div>
									))
								)}
							</div>
						</div>
						{/* Journalling Prompts Column */}
						<div className='flex-1 bg-greyBackground min-h-[100%] p-4 rounded-xl flex flex-col'>
							<div className='relative mb-4'>
								<h3 className='text-center text-[20px] font-semibold text-darkenPurple truncate px-[30px]'>Journalling Prompts</h3>
								<button onClick={() => id && openAddModal('journallingPrompts', id)} className='absolute right-0 top-0 rounded-full bg-white w-[30px] h-[30px] font-bold text-darkenPurple hover:bg-primaryViolet hover:text-white transition'>
									+
								</button>
							</div>
							<div className='flex-1 overflow-y-auto flex flex-col gap-2'>
								{loadingJournallingPrompts ? (
									<div className='flex items-center justify-center py-8'>
										<Loading size={40} primaryColor='#5E639B' stroke={2} />
									</div>
								) : journallingPrompts.length === 0 ? (
									<p className='text-center text-mudPurple text-[14px] py-4'>No journalling prompts added yet</p>
								) : (
									journallingPrompts.map((tip: any) => (
										<div key={tip.id} className='bg-white rounded-lg p-3 flex flex-col gap-1 group hover:shadow-md transition'>
											<div className='flex justify-between items-start'>
												<div className='flex-1'>
													<p className='text-[14px] font-semibold text-primaryViolet'>{tip.text}</p>
												</div>
												<button onClick={() => openDeleteModal(tip.id, 'journallingPrompt', tip.text, tip.recommendationId)} className='opacity-0 group-hover:opacity-100 transition ml-2'>
													<DeleteIcon className='w-4 h-4 text-red-500 hover:text-red-700' />
												</button>
											</div>
										</div>
									))
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Add Content Modal */}
			{addContentModal.open && <AddContentModal modalIsOpen={addContentModal.open} closeModal={() => setAddContentModal({ ...addContentModal, open: false })} onSuccess={loadRecommendations} recommendationId={addContentModal.recommendationId} contentType={addContentModal.type} existingIds={getExistingIds(addContentModal.type)} />}

			{/* Delete Confirmation Modal */}
			<ConfirmationModal modalIsOpen={deleteModal.open} closeModal={() => setDeleteModal({ ...deleteModal, open: false })} confirmation={handleDeleteContent} message={`Are you sure you want to remove "${deleteModal.itemName}"?`} />
		</div>
	);
}

export default SingleStruggleRecommendationScreen;
